import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  Megaphone,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  ShieldCheck,
  Clock,
  FolderPlus,
  FolderUp,
  Search,
  MinusCircle,
} from "lucide-react";
import { fileApi, folderApi, chatApiInstance } from "../services/api";
import FileUpload from "./FileUpload";
import FileList from "./FileList";
import VtexPromotionSync from "./VtexPromotionSync";
import FolderBreadcrumbs from "./FolderBreadcrumbs";
import { useLanguage } from "../contexts/LanguageContext";
import { useUpload } from "../contexts/UploadContext";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";

/* ─── Collection metadata ─── */
const COLLECTION_META = {
  general_information: {
    icon: FolderOpen,
  },
  campaigns: {
    icon: Megaphone,
  },
  internal_procedures: {
    icon: ShieldCheck,
  },
};

const FileManagement = () => {
  const { translate } = useLanguage();
  const { user, isInternalProceduresManager, canManageCollection } = useAuth();
  const [availableChats, setAvailableChats] = useState([]);
  const {
    clearUploadStatus,
    setUploadingState,
    error,
    setError,
    fileQueue,
    clearFileQueue,
    deleteQueue,
    clearDeleteQueue,
  } = useUpload();
  const [queueCollapsed, setQueueCollapsed] = useState(false);
  const [deleteQueueCollapsed, setDeleteQueueCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Parse URL to get collection and folder
  // URL formats: /files/{collection} or /files/{collection}/folder/{folderId}
  const pathParts = location.pathname.split("/").filter(Boolean);
  const currentCollection = pathParts[1] || "general_information";
  const currentFolderId = pathParts[2] === "folder" ? pathParts[3] : null;
  const [activeCollection, setActiveCollection] = useState(currentCollection);

  const [files, setFiles] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [hasMoreFiles, setHasMoreFiles] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const supportedTypes = [".md", ".txt", ".pdf"];
  const [createFolderTrigger, setCreateFolderTrigger] = useState(0);
  const [refreshFoldersTrigger, setRefreshFoldersTrigger] = useState(0);
  const [nameFilter, setNameFilter] = useState("");
  const [isSavingFolder, setIsSavingFolder] = useState(false);
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  const [isDraggingOverFolder, setIsDraggingOverFolder] = useState(false);
  const [movingFilePaths, setMovingFilePaths] = useState(new Set());
  const [movingFolderIds, setMovingFolderIds] = useState(new Set());
  const [removeFolderIds, setRemoveFolderIds] = useState([]);
  const [hasInternalChats, setHasInternalChats] = useState(false);
  const [tabsLoaded, setTabsLoaded] = useState(false);

  /* ─── Collections the user may manage (advisory mirror; backend enforces) ─── */
  const collections = useMemo(
    () =>
      [
        { key: "general_information" },
        { key: "campaigns" },
        ...(hasInternalChats ? [{ key: "internal_procedures" }] : []),
      ].filter((c) => canManageCollection(c.key)),
    [hasInternalChats, canManageCollection]
  );

  // Advisory mirror (spec §3): whether the user may WRITE in the current
  // location. Non-managers can write only inside an internal_procedures
  // department subtree whose role they hold; the collection root and open
  // (unbound) subtrees are read-only. The backend is the enforcement boundary,
  // this only hides 403 dead-ends. `currentFolderDept` holds the current
  // folder's resolved department from the backend: a role string (bound), null
  // (open/unbound), or `undefined` when unknown — still loading OR served by a
  // backend that predates the field. Unknown falls back to PERMISSIVE so we
  // never wrongly hide a legitimate in-subtree action (deploy-order safe).
  const [currentFolderDept, setCurrentFolderDept] = useState(undefined);
  useEffect(() => {
    if (activeCollection !== "internal_procedures" || !currentFolderId) {
      setCurrentFolderDept(null);
      return;
    }
    setCurrentFolderDept(undefined);
    let cancelled = false;
    folderApi
      .getFolder(currentFolderId)
      .then((f) => {
        if (!cancelled) setCurrentFolderDept(f?.department_required_role);
      })
      .catch(() => {
        if (!cancelled) setCurrentFolderDept(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [activeCollection, currentFolderId]);

  const locationWritable =
    activeCollection !== "internal_procedures" ||
    isInternalProceduresManager() ||
    (currentFolderId
      ? currentFolderDept === undefined
        ? true // unknown (loading or pre-field backend) → permissive fallback
        : typeof currentFolderDept === "string" &&
          (user?.roles || []).includes(currentFolderDept)
      : false); // internal_procedures collection root → read-only for non-managers

  const lastFetchOptions = useRef({});
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesLoadingMore, setFilesLoadingMore] = useState(false);
  const abortControllerRef = useRef(null);
  const fileUploadRef = useRef(null);
  const uploadInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const uploadMenuRef = useRef(null);
  const uploadQueueListRef = useRef(null);

  const fetchFiles = useCallback(
    async (collection = activeCollection, options = {}) => {
      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      const isLoadMore = !!options.cursor;
      if (isLoadMore) {
        setFilesLoadingMore(true);
      } else {
        setFilesLoading(true);
        // Clear stale data immediately so old files don't flash
        setFiles([]);
        setTotalFiles(0);
        setHasMoreFiles(false);
        setNextCursor(null);
      }
      setError(null);
      lastFetchOptions.current = options;
      try {
        const fetchOptions = { ...options };
        if (fetchOptions.folderId === undefined) {
          fetchOptions.folderId = currentFolderId || null;
        }
        const result = await fileApi.listFiles(collection, null, fetchOptions, signal);
        if (signal.aborted) return;

        if (isLoadMore) {
          // Append mode for infinite scroll
          setFiles((prev) => [...prev, ...(result.files || [])]);
        } else {
          // Replace mode for initial load / refresh
          setFiles(result.files || []);
        }
        setTotalFiles(result.total_files || 0);
        setHasMoreFiles(result.has_more || false);
        setNextCursor(result.next_cursor || null);
      } catch (err) {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        console.error("Error fetching files:", err);
        setError(
          err.response?.data?.detail ||
            err.message ||
            translate("failedToLoadFiles")
        );
      } finally {
        if (!signal.aborted) {
          setFilesLoading(false);
          setFilesLoadingMore(false);
        }
      }
    },
    [activeCollection, currentFolderId, translate, setError]
  );

  // Silent refresh: re-fetches files without clearing the list or showing loading skeletons
  const silentRefresh = useCallback(
    async (collection = activeCollection, options = {}) => {
      try {
        const fetchOptions = { ...options };
        if (fetchOptions.folderId === undefined) {
          fetchOptions.folderId = currentFolderId || null;
        }
        const result = await fileApi.listFiles(collection, null, fetchOptions);
        setFiles(result.files || []);
        setTotalFiles(result.total_files || 0);
        setHasMoreFiles(result.has_more || false);
        setNextCursor(result.next_cursor || null);
      } catch {
        // Silent — don't show errors for background syncs
      }
    },
    [activeCollection, currentFolderId]
  );

  const fetchMoreFiles = useCallback(() => {
    if (!nextCursor || filesLoadingMore) return;
    fetchFiles(activeCollection, { ...lastFetchOptions.current, cursor: nextCursor });
  }, [nextCursor, filesLoadingMore, activeCollection, fetchFiles]);


  const loadChatsForTenant = useCallback(async () => {
    try {
      const response = await chatApiInstance.get("/");
      const chatsData = response.data?.chats || [];
      setAvailableChats(chatsData);
      const hasInternal = chatsData.some(
        (chat) => chat.internal_chat === true
      );
      setHasInternalChats(hasInternal);
      setTabsLoaded(true);
    } catch (err) {
      console.error("FileManagement - Error loading chats:", err);
      setTabsLoaded(true);
    }
  }, []);

  // Auto-scroll upload queue to the active (uploading) item
  useEffect(() => {
    if (!uploadQueueListRef.current) return;
    const activeIndex = fileQueue.findIndex((f) => f.status === "uploading");
    if (activeIndex === -1) return;
    const rows = uploadQueueListRef.current.children;
    if (rows[activeIndex]) {
      rows[activeIndex].scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [fileQueue]);

  // Close upload menu on outside click
  useEffect(() => {
    if (!showUploadMenu) return;
    const handler = (e) => {
      if (uploadMenuRef.current && !uploadMenuRef.current.contains(e.target)) {
        setShowUploadMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showUploadMenu]);

  // Load chats once on mount; abort in-flight fetches on unmount
  useEffect(() => {
    loadChatsForTenant();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      clearFileQueue();
      clearDeleteQueue();
    };
  }, [loadChatsForTenant]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear completed queues when switching collection
  useEffect(() => {
    clearFileQueue();
    clearDeleteQueue();
  }, [activeCollection]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFetchFiles = useCallback(
    (options = {}) => {
      fetchFiles(activeCollection, options);
    },
    [activeCollection, fetchFiles]
  );

  const handleFileUploaded = () => {
    const { cursor, ...optsWithoutCursor } = lastFetchOptions.current;
    fetchFiles(activeCollection, optsWithoutCursor);
    setRefreshFoldersTrigger((c) => c + 1);
    setUploadingState(false);
  };

  // Diz ao painel da VTEX que os ficheiros mudaram por baixo dele. A lista já se
  // refresca em cada uma destas acções, mas o painel lê os MESMOS ficheiros para
  // contar o que está a funcionar e o que falta aprovar, e não tinha como saber —
  // era isso, e não a sincronização, que sobrava para o botão de Atualizar fazer.
  //
  // Um contador e não um booleano: duas remoções seguidas têm de ser dois avisos.
  const [filesChangedAt, setFilesChangedAt] = useState(0);
  const noteFilesChanged = useCallback(() => setFilesChangedAt((n) => n + 1), []);

  const handleFileDeleted = () => {
    noteFilesChanged();
    const { cursor, ...optsWithoutCursor } = lastFetchOptions.current;
    fetchFiles(activeCollection, optsWithoutCursor);
  };

  const handleFileUpdated = (filePath, updateResult) => {
    // Inclui atribuir ou tirar um chat a um ficheiro pelo "Gerir Chats", que é
    // aprovar ou desaprovar uma campanha por outro caminho que não o painel.
    noteFilesChanged();
    const { cursor, ...optsWithoutCursor } = lastFetchOptions.current;
    fetchFiles(activeCollection, optsWithoutCursor);
  };

  // Upload OS files/folders to a specific folder (called from folder drop targets)
  const handleUploadToFolder = useCallback((osFiles, targetFolderId, dataTransferItems) => {
    // Clear the drag overlay since stopPropagation prevents the container's onDrop from firing
    dragCounterRef.current = 0;
    setIsDragOver(false);

    // Check for folder drops if dataTransferItems provided
    if (dataTransferItems) {
      const entries = Array.from(dataTransferItems).map((item) => item.webkitGetAsEntry?.()).filter(Boolean);
      const hasDirectories = entries.some((entry) => entry.isDirectory);
      const hasFiles = entries.some((entry) => entry.isFile);

      if (hasDirectories && hasFiles) {
        toast.error(translate("mixedDropError"));
        return;
      }
      if (hasDirectories) {
        fileUploadRef.current?.handleFolderUpload(entries, targetFolderId);
        return;
      }
    }

    fileUploadRef.current?.handleFiles(osFiles, targetFolderId);
  }, [translate]);

  const handleRefresh = () => {
    // Always refresh from the beginning (no cursor) to get a clean list
    const { cursor, ...optsWithoutCursor } = lastFetchOptions.current;
    fetchFiles(activeCollection, optsWithoutCursor);
  };

  // Optimistic removal: instantly remove moved/deleted files from UI, then silent background refresh
  const handleOptimisticRemoveFiles = useCallback((filePaths) => {
    setFiles((prev) => prev.filter((f) => !filePaths.includes(f.path)));
    setTotalFiles((prev) => Math.max(0, prev - filePaths.length));
    setMovingFilePaths(new Set());
    noteFilesChanged();
    // Silent background refresh to sync cursors/counts without clearing list
    const { cursor, ...optsWithoutCursor } = lastFetchOptions.current;
    silentRefresh(activeCollection, optsWithoutCursor);
  }, [activeCollection, silentRefresh, noteFilesChanged]);

  // O painel da VTEX reconcilia-se sozinho e chama isto no fim de uma rajada de
  // aprovações. Silencioso, e não o `handleRefresh`: esse limpa a lista e volta a
  // pintar o skeleton — o piscar que o debounce do painel existe para evitar.
  const handleSyncedFilesChanged = useCallback(() => {
    const { cursor, ...optsWithoutCursor } = lastFetchOptions.current;
    silentRefresh(activeCollection, optsWithoutCursor);
  }, [activeCollection, silentRefresh]);

  const handleOptimisticRemoveFolders = useCallback((folderIds) => {
    // Instantly remove folders from FileList + background refresh for counts
    if (folderIds && folderIds.length > 0) {
      setRemoveFolderIds(folderIds);
    }
    // Apagar a pasta VTEX leva com ela todas as campanhas sincronizadas.
    noteFilesChanged();
    setRefreshFoldersTrigger((c) => c + 1);
    const { cursor, ...optsWithoutCursor } = lastFetchOptions.current;
    silentRefresh(activeCollection, optsWithoutCursor);
  }, [activeCollection, silentRefresh, noteFilesChanged]);

  const handleCollectionChange = (collection) => {
    setActiveCollection(collection);
    setNameFilter("");
    navigate(`/files/${collection}`);
  };

  const handleNavigateToFolder = useCallback((folderId) => {
    if (folderId) {
      navigate(`/files/${activeCollection}/folder/${folderId}`);
    } else {
      navigate(`/files/${activeCollection}`);
    }
  }, [activeCollection, navigate]);

  useEffect(() => {
    // Redirect bare /files to the first collection the user may see
    if (location.pathname === "/files" || location.pathname === "/files/") {
      if (collections.length > 0) {
        navigate(`/files/${collections[0].key}`, { replace: true });
      }
      return;
    }
    // Sync active collection from URL (single source of truth)
    setActiveCollection(currentCollection);
  }, [currentCollection, location.pathname, navigate, collections]);

  // Redirect away from a collection the user may not see. Advisory only:
  // the backend also rejects the underlying API calls.
  useEffect(() => {
    if (!tabsLoaded || collections.length === 0) return;
    const allowedKeys = collections.map((c) => c.key);
    if (currentCollection && !allowedKeys.includes(currentCollection)) {
      navigate(`/files/${collections[0].key}`, { replace: true });
    }
  }, [tabsLoaded, currentCollection, collections, navigate]);

  const getCollectionDisplayName = (collection) => {
    switch (collection) {
      case "general_information":
        return translate("generalInformation");
      case "campaigns":
        return translate("campaigns");
      case "internal_procedures":
        return translate("internalProcedures");
      default:
        return collection;
    }
  };

  const activeMeta = COLLECTION_META[activeCollection] || COLLECTION_META.general_information;
  const ActiveIcon = activeMeta.icon;

  /* ─── Queue panels (passed to FileList to render between folders and files) ─── */
  const queuePanelsJsx = (
    <>
      {/* Delete Queue Panel */}
      {deleteQueue.length > 0 && (
        <div style={{ borderBottom: "1px solid var(--color-border)" }}>
          <div
            className="px-4 py-3 flex items-center justify-between cursor-pointer"
            style={{ borderBottom: deleteQueueCollapsed ? "none" : "1px solid var(--color-border)" }}
            onClick={() => setDeleteQueueCollapsed(!deleteQueueCollapsed)}
          >
            <div className="flex items-center gap-2.5">
              {(() => {
                const done = deleteQueue.filter((f) => f.status === "success").length;
                const failed = deleteQueue.filter((f) => f.status === "error").length;
                // Rejeitadas contam como resolvidas mas NÃO como apagadas: uma
                // campanha da VTEX é partilhada e o ficheiro fica. Sem isto a fila
                // dizia "eliminado" e riscava o nome de um ficheiro que continua lá.
                const rejected = deleteQueue.filter((f) => f.status === "rejected").length;
                const total = deleteQueue.length;
                const allDone = done + failed + rejected === total;
                return (
                  <>
                    {allDone ? (
                      failed > 0 ? <AlertCircle size={16} style={{ color: "var(--color-error)" }} /> : <CheckCircle size={16} style={{ color: "var(--color-mint)" }} />
                    ) : (
                      <div className="flex-shrink-0" style={{ width: 16, height: 16, border: "2px solid var(--color-error)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                    )}
                    <span className="text-sm font-medium" style={{ color: allDone ? (failed > 0 ? "var(--color-error)" : "var(--color-mint-dark)") : "var(--color-text-primary)" }}>
                      {allDone
                        ? failed > 0
                          ? translate("deleteComplete", { successful: done, failed })
                          : rejected > 0 && done > 0
                            ? translate("deleteAndRejectComplete", { deleted: done, rejected, plural: rejected > 1 ? "s" : "", pluralD: done > 1 ? "s" : "" })
                            : rejected > 0
                              ? translate(rejected === 1 ? "campaignRejectedForStore" : "campaignsRejectedForStore", { count: rejected })
                              : translate("allFilesDeletedSuccessfully", { count: done, plural: done > 1 ? "s" : "" })
                        : translate("deletingFiles", { fileName: deleteQueue.find((f) => f.status === "deleting")?.name || "", current: done + failed + rejected + 1, total })}
                    </span>
                  </>
                );
              })()}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: "var(--color-surface-raised)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border)" }}>
                {deleteQueue.filter((f) => f.status === "success" || f.status === "rejected").length}/{deleteQueue.length}
              </span>
              <ChevronDown size={14} style={{ color: "var(--color-text-muted)", transform: deleteQueueCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
              {deleteQueue.filter((f) => ["success", "error", "rejected"].includes(f.status)).length === deleteQueue.length && (
                <button onClick={(e) => { e.stopPropagation(); clearDeleteQueue(); }} className="p-1 rounded-lg transition-colors" style={{ color: "var(--color-text-muted)" }}><X size={14} /></button>
              )}
            </div>
          </div>
          {!deleteQueueCollapsed && (
            <div className="max-h-64 overflow-y-auto">
              {deleteQueue.map((file, index) => (
                <div key={file.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" style={{ borderBottom: index < deleteQueue.length - 1 ? "1px solid var(--color-border)" : "none", backgroundColor: file.status === "deleting" ? "rgba(239,68,68,0.04)" : "transparent" }}>
                  <div className="flex-shrink-0 w-5 flex items-center justify-center">
                    {file.status === "queued" && <Clock size={14} style={{ color: "var(--color-text-muted)" }} />}
                    {file.status === "deleting" && <div style={{ width: 14, height: 14, border: "2px solid var(--color-error)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />}
                    {file.status === "success" && <CheckCircle size={14} style={{ color: "var(--color-mint)" }} />}
                    {file.status === "rejected" && <MinusCircle size={14} style={{ color: "var(--color-amber-dark)" }} />}
                    {file.status === "error" && <AlertCircle size={14} style={{ color: "var(--color-error)" }} />}
                  </div>
                  <span className="flex-1 min-w-0 truncate" style={{ color: file.status === "error" ? "var(--color-error)" : file.status === "success" ? "var(--color-text-muted)" : file.status === "deleting" ? "var(--color-text-primary)" : "var(--color-text-secondary)", textDecoration: file.status === "success" ? "line-through" : "none" }}>{file.name}</span>
                  <span className="text-xs flex-shrink-0 px-2 py-0.5 rounded-full" style={file.status === "queued" ? { backgroundColor: "var(--color-surface-raised)", color: "var(--color-text-muted)" } : file.status === "deleting" ? { backgroundColor: "rgba(239,68,68,0.08)", color: "var(--color-error)" } : file.status === "success" ? { backgroundColor: "rgba(62,207,142,0.1)", color: "var(--color-mint)" } : file.status === "rejected" ? { backgroundColor: "var(--color-amber-light)", color: "var(--color-amber-text)" } : { backgroundColor: "rgba(239,68,68,0.08)", color: "var(--color-error)" }}>
                    {file.status === "queued" && translate("queued")}{file.status === "deleting" && translate("deleting")}{file.status === "success" && translate("deleted")}{file.status === "rejected" && translate("rejected")}{file.status === "error" && translate("failed")}
                  </span>
                </div>
              ))}
            </div>
          )}
          {deleteQueue.some((f) => f.status === "deleting" || f.status === "queued") && (
            <div style={{ height: 3, backgroundColor: "var(--color-surface-raised)" }}>
              <div style={{ height: "100%", width: `${(deleteQueue.filter((f) => f.status === "success" || f.status === "error").length / deleteQueue.length) * 100}%`, backgroundColor: "var(--color-error)", transition: "width 0.3s ease", borderRadius: "0 2px 2px 0" }} />
            </div>
          )}
        </div>
      )}

      {/* Upload Queue Panel */}
      {fileQueue.length > 0 && (
        <div style={{ borderBottom: "1px solid var(--color-border)" }}>
          <div
            className="px-4 py-3 flex items-center justify-between cursor-pointer"
            style={{ borderBottom: queueCollapsed ? "none" : "1px solid var(--color-border)" }}
            onClick={() => setQueueCollapsed(!queueCollapsed)}
          >
            <div className="flex items-center gap-2.5">
              {(() => {
                const done = fileQueue.filter((f) => f.status === "success").length;
                const failed = fileQueue.filter((f) => f.status === "error").length;
                const total = fileQueue.length;
                const allDone = done + failed === total;
                return (
                  <>
                    {allDone ? (
                      failed > 0 ? <AlertCircle size={16} style={{ color: "var(--color-error)" }} /> : <CheckCircle size={16} style={{ color: "var(--color-mint)" }} />
                    ) : (
                      <div className="flex-shrink-0" style={{ width: 16, height: 16, border: "2px solid var(--color-mint)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                    )}
                    <span className="text-sm font-medium" style={{ color: allDone ? (failed > 0 ? "var(--color-error)" : "var(--color-mint-dark)") : "var(--color-text-primary)" }}>
                      {allDone
                        ? failed > 0
                          ? translate("uploadComplete", { successful: done, failed })
                          : translate("allFilesUploadedSuccessfully", { count: done, plural: done > 1 ? "s" : "" })
                        : translate("uploadingAndProcessing", { fileName: fileQueue.find((f) => f.status === "uploading")?.name || "", current: done + failed + 1, total })}
                    </span>
                  </>
                );
              })()}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: "var(--color-surface-raised)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border)" }}>
                {fileQueue.filter((f) => f.status === "success").length}/{fileQueue.length}
              </span>
              <ChevronDown size={14} style={{ color: "var(--color-text-muted)", transform: queueCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
              {fileQueue.filter((f) => f.status === "success" || f.status === "error").length === fileQueue.length && (
                <button onClick={(e) => { e.stopPropagation(); clearFileQueue(); clearUploadStatus(); }} className="p-1 rounded-lg transition-colors" style={{ color: "var(--color-text-muted)" }}><X size={14} /></button>
              )}
            </div>
          </div>
          {!queueCollapsed && (
            <div ref={uploadQueueListRef} className="max-h-64 overflow-y-auto">
              {fileQueue.map((file, index) => (
                <div key={file.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" style={{ borderBottom: index < fileQueue.length - 1 ? "1px solid var(--color-border)" : "none", backgroundColor: file.status === "uploading" ? "var(--color-mint-muted)" : "transparent" }}>
                  <div className="flex-shrink-0 w-5 flex items-center justify-center">
                    {file.status === "queued" && <Clock size={14} style={{ color: "var(--color-text-muted)" }} />}
                    {file.status === "uploading" && <div style={{ width: 14, height: 14, border: "2px solid var(--color-mint)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />}
                    {file.status === "success" && <CheckCircle size={14} style={{ color: "var(--color-mint)" }} />}
                    {file.status === "error" && <AlertCircle size={14} style={{ color: "var(--color-error)" }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="block truncate" style={{ color: file.status === "error" ? "var(--color-error)" : file.status === "success" ? "var(--color-mint-dark)" : file.status === "uploading" ? "var(--color-text-primary)" : "var(--color-text-secondary)" }}>{file.name}</span>
                    {file.status === "error" && file.error && <span className="block text-xs mt-0.5 truncate" style={{ color: "var(--color-error)", opacity: 0.8 }}>{file.error}</span>}
                  </div>
                  <span className="text-xs flex-shrink-0" style={{ color: "var(--color-text-muted)" }}>{file.size < 1024 ? `${file.size} B` : file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)} KB` : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}</span>
                  <span className="text-xs flex-shrink-0 px-2 py-0.5 rounded-full" style={file.status === "queued" ? { backgroundColor: "var(--color-surface-raised)", color: "var(--color-text-muted)" } : file.status === "uploading" ? { backgroundColor: "rgba(62,207,142,0.15)", color: "var(--color-mint-dark)" } : file.status === "success" ? { backgroundColor: "rgba(62,207,142,0.1)", color: "var(--color-mint)" } : { backgroundColor: "rgba(239,68,68,0.08)", color: "var(--color-error)" }}>
                    {file.status === "queued" && translate("queued")}{file.status === "uploading" && translate("uploading")}{file.status === "success" && translate("completed")}{file.status === "error" && translate("failed")}
                  </span>
                </div>
              ))}
            </div>
          )}
          {fileQueue.some((f) => f.status === "uploading" || f.status === "queued") && (
            <div style={{ height: 3, backgroundColor: "var(--color-surface-raised)" }}>
              <div style={{ height: "100%", width: `${(fileQueue.filter((f) => f.status === "success" || f.status === "error").length / fileQueue.length) * 100}%`, backgroundColor: "var(--color-mint)", transition: "width 0.3s ease", borderRadius: "0 2px 2px 0" }} />
            </div>
          )}
        </div>
      )}
    </>
  );

  /* ─── Loading skeleton ─── */
  // On a bare /files path the redirect effect navigates to the first allowed
  // collection; keep the skeleton up while that redirect is pending so we
  // never flash the default-collection header (hits internal_procedures-only
  // users, whose collection resolves only once tabs load). The chatless
  // empty-collections edge (collections stays []) falls through to render.
  const onBareFilesPath =
    location.pathname === "/files" || location.pathname === "/files/";
  if (!tabsLoaded || (onBareFilesPath && collections.length > 0)) {
    return (
      <div className="space-y-6 page-enter animate-pulse">
        <div className="pb-5" style={{ borderBottom: "1px solid var(--color-border)" }}>
          <div className="h-7 w-64 rounded-lg mb-2 bg-[var(--color-skeleton)]" />
          <div className="h-4 w-96 rounded bg-[var(--color-skeleton-light)]" />
        </div>
        <div className="flex gap-6">
          <div className="w-64 flex-shrink-0 space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 rounded-xl bg-[var(--color-skeleton-light)]" />
            ))}
          </div>
          <div className="flex-1 space-y-4">
            <div className="h-16 rounded-xl bg-[var(--color-skeleton-light)]" />
            <div className="h-48 rounded-xl bg-[var(--color-skeleton-light)]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 page-enter">
      {/* Page header */}
      <div
        className="pb-5"
        style={{ borderBottom: "1px solid var(--color-border)" }}
      >
        <h1
          className="text-2xl font-semibold"
          style={{ color: "var(--color-text-primary)" }}
        >
          {translate("fileManagement")}
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
          {translate("fileManagementDescription")}
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div
          className="rounded-xl px-4 py-3 flex items-center gap-2 text-sm"
          style={{
            backgroundColor: "rgba(239,68,68,0.06)",
            border: "1px solid rgba(239,68,68,0.15)",
            color: "var(--color-error)",
          }}
        >
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {/* Main layout: Sidebar nav + Content */}
      <div className="flex gap-6">
        {/* ─── Left: Collections nav ─── */}
        <nav className="w-64 flex-shrink-0 space-y-1">
          {collections.map(({ key }) => {
            const meta = COLLECTION_META[key] || {};
            const Icon = meta.icon || FolderOpen;
            const isActive = activeCollection === key;

            return (
              <button
                key={key}
                onClick={() => handleCollectionChange(key)}
                className="w-full text-left rounded-xl px-4 py-3 transition-all duration-150 group relative"
                style={{
                  backgroundColor: isActive
                    ? "var(--color-mint-muted)"
                    : "transparent",
                  border: isActive
                    ? "1px solid var(--color-mint-border)"
                    : "1px solid transparent",
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                    style={{
                      backgroundColor: isActive
                        ? "var(--color-mint-muted)"
                        : "var(--color-surface-raised)",
                    }}
                  >
                    <Icon
                      size={16}
                      style={{
                        color: isActive
                          ? "var(--color-mint)"
                          : "var(--color-text-muted)",
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-medium truncate"
                      style={{
                        color: isActive
                          ? "var(--color-text-primary)"
                          : "var(--color-text-secondary)",
                      }}
                    >
                      {getCollectionDisplayName(key)}
                    </p>
                  </div>
                  {isActive && (
                    <ChevronRight
                      size={14}
                      style={{ color: "var(--color-mint)" }}
                    />
                  )}
                </div>
              </button>
            );
          })}
        </nav>

        {/* ─── Right: Content area ─── */}
        <div className="flex-1 min-w-0">
          <div
            className="rounded-xl overflow-hidden relative"
            style={{
              backgroundColor: "var(--color-surface)",
              border: isDragOver ? "2px dashed var(--color-mint)" : "1px solid var(--color-border)",
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              // Only show upload overlay for OS file drags, not internal item drags
              // Internal drags set "application/json" type — skip those
              const types = Array.from(e.dataTransfer.types);
              if (types.includes("application/json")) return;
              dragCounterRef.current++;
              setIsDragOver(true);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={(e) => {
              e.preventDefault();
              if (!isDragOver) return;
              dragCounterRef.current--;
              if (dragCounterRef.current === 0) setIsDragOver(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              dragCounterRef.current = 0;
              setIsDragOver(false);
              // Only handle OS file drops, not internal item moves
              const types = Array.from(e.dataTransfer.types);
              if (types.includes("application/json")) return;

              // Check for folder drops via webkitGetAsEntry
              const items = Array.from(e.dataTransfer.items || []);
              const entries = items.map((item) => item.webkitGetAsEntry?.()).filter(Boolean);
              const hasDirectories = entries.some((entry) => entry.isDirectory);
              const hasFiles = entries.some((entry) => entry.isFile);

              if (hasDirectories && hasFiles) {
                // Mixed drop — reject
                toast.error(translate("mixedDropError"));
                return;
              }

              if (hasDirectories) {
                // Folder upload flow
                fileUploadRef.current?.handleFolderUpload(entries);
                return;
              }

              // Regular file drop
              const droppedFiles = Array.from(e.dataTransfer.files);
              if (droppedFiles.length > 0) fileUploadRef.current?.handleFiles(droppedFiles);
            }}
          >
            {/* Drag overlay — pointer-events-none so folder rows underneath still receive drag events */}
            {isDragOver && !isDraggingOverFolder && (
              <div
                className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl pointer-events-none"
                style={{
                  backgroundColor: "rgba(62, 207, 142, 0.08)",
                }}
              >
                <Upload size={40} style={{ color: "var(--color-mint)" }} />
                <p
                  className="mt-3 text-sm font-medium"
                  style={{ color: "var(--color-mint)" }}
                >
                  {translate("dropFilesHere") || "Drop files here"}
                </p>
              </div>
            )}

            {/* Content header */}
            <div
              className="px-6 py-5 flex items-center justify-between"
              style={{ borderBottom: "1px solid var(--color-border)" }}
            >
              <div className="flex items-center gap-4 min-w-0 flex-1">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "var(--color-mint-muted)" }}
                >
                  <ActiveIcon
                    size={20}
                    style={{ color: "var(--color-mint)" }}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  {currentFolderId ? (
                    <FolderBreadcrumbs
                      folderId={currentFolderId}
                      collectionName={activeCollection}
                      collectionDisplayName={getCollectionDisplayName(activeCollection)}
                      onNavigate={handleNavigateToFolder}
                      onRefresh={handleRefresh}
                      translate={translate}
                      isDraggingItem={isDraggingItem}
                      onUploadToFolder={handleUploadToFolder}
                      setMovingFilePaths={setMovingFilePaths}
                      setMovingFolderIds={setMovingFolderIds}
                      onOptimisticRemoveFiles={handleOptimisticRemoveFiles}
                      onOptimisticRemoveFolders={handleOptimisticRemoveFolders}
                    />

                  ) : (
                    <h2
                      className="text-lg font-semibold"
                      style={{ color: "var(--color-text-primary)" }}
                    >
                      {getCollectionDisplayName(activeCollection)}
                    </h2>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--color-text-muted)" }} />
                  <input
                    type="text"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    placeholder={translate("enterFileName")}
                    className="field-input pl-8 pr-8 py-1.5 text-sm w-56"
                    onKeyDown={(e) => { if (e.key === "Escape") setNameFilter(""); }}
                  />
                  {nameFilter && (
                    <button
                      onClick={() => setNameFilter("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-[var(--color-surface-raised)]"
                    >
                      <X size={12} style={{ color: "var(--color-text-muted)" }} />
                    </button>
                  )}
                </div>
                {locationWritable && (
                  <>
                    <button
                      onClick={() => setCreateFolderTrigger((c) => c + 1)}
                      disabled={isSavingFolder}
                      className="btn-secondary flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <FolderPlus size={14} />
                      <span>{translate("newFolder")}</span>
                    </button>
                    {/* Upload dropdown */}
                    <div className="relative" ref={uploadMenuRef}>
                      <button
                        onClick={() => setShowUploadMenu((v) => !v)}
                        className="btn-secondary flex items-center gap-2"
                      >
                        <Upload size={14} />
                        <span>{translate("upload")}</span>
                        <ChevronDown size={12} style={{ marginLeft: -2 }} />
                      </button>
                      {showUploadMenu && (
                        <div
                          className="absolute right-0 mt-1 w-44 rounded-xl border bg-[var(--color-surface)] shadow-lg z-50 py-1"
                          style={{ borderColor: "var(--color-border)" }}
                        >
                          <button
                            className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-surface-raised)] flex items-center gap-2 transition-colors"
                            style={{ color: "var(--color-text-secondary)" }}
                            onClick={() => { setShowUploadMenu(false); uploadInputRef.current?.click(); }}
                          >
                            <Upload size={14} />
                            {translate("uploadFiles")}
                          </button>
                          <button
                            className="w-full text-left px-4 py-2 text-sm hover:bg-[var(--color-surface-raised)] flex items-center gap-2 transition-colors"
                            style={{ color: "var(--color-text-secondary)" }}
                            onClick={() => { setShowUploadMenu(false); fileUploadRef.current?.openFolderPicker(); }}
                          >
                            <FolderUp size={14} />
                            {translate("uploadFolder")}
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
                <input
                  ref={uploadInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept={supportedTypes.join(",")}
                  onChange={(e) => {
                    const droppedFiles = Array.from(e.target.files);
                    if (droppedFiles.length > 0) fileUploadRef.current?.handleFiles(droppedFiles);
                    e.target.value = null;
                  }}
                />
              </div>
            </div>

            {/* Hidden FileUpload — handles upload logic and modals */}
            <div className="hidden">
              <FileUpload
                ref={fileUploadRef}
                onFileUploaded={handleFileUploaded}
                onUploadStarted={() => setQueueCollapsed(false)}
                supportedTypes={supportedTypes}
                collectionName={activeCollection}
                availableChats={availableChats}
                folderId={currentFolderId}
              />
            </div>

            {/* O que a VTEX tem e uma lista de ficheiros não consegue mostrar: a
                fila de aprovação, o que ficou de fora e a última sincronização.
                O próprio painel decide se se desenha — pergunta ao backend se este
                chat é uma loja VTEX. */}
            {/* Só na RAIZ das campanhas. Dentro de uma pasta, isto era uma secção
                inteira encravada entre o breadcrumb e os ficheiros, a falar de uma
                coisa que não é a pasta onde se está: quem entra em "Campanhas > VTEX"
                foi ver ficheiros, e a fila de aprovação não muda com a pasta.

                O sítio dele é o nível da secção, que é onde se decide o que entra na
                coleção. Descer uma pasta é descer para o conteúdo. */}
            {activeCollection === "campaigns" && !currentFolderId && (
              <VtexPromotionSync
                availableChats={availableChats}
                onFilesChanged={handleSyncedFilesChanged}
                filesChangedAt={filesChangedAt}
              />
            )}

            {/* File List */}
            <FileList
              key={`${activeCollection}-${currentFolderId || "root"}`}
              files={files}
              totalFiles={totalFiles}
              isLoading={filesLoading}
              isLoadingMore={filesLoadingMore}
              hasMore={hasMoreFiles}
              onLoadMore={fetchMoreFiles}
              isDraggingItem={isDraggingItem}
              onDraggingChange={setIsDraggingItem}
              onDraggingOverFolderChange={setIsDraggingOverFolder}
              onUploadToFolder={handleUploadToFolder}
              onFetchFiles={handleFetchFiles}
              onFileDeleted={handleFileDeleted}
              onRefresh={handleRefresh}
              onFileUpdated={handleFileUpdated}
              collectionName={activeCollection}
              availableChats={availableChats}
              folderId={currentFolderId}
              locationWritable={locationWritable}
              onNavigateToFolder={handleNavigateToFolder}
              createFolderTrigger={createFolderTrigger}
              nameFilter={nameFilter}
              setNameFilter={setNameFilter}
              refreshFoldersTrigger={refreshFoldersTrigger}
              queuePanels={queuePanelsJsx}
              onSavingFolderChange={setIsSavingFolder}
              movingFilePaths={movingFilePaths}
              setMovingFilePaths={setMovingFilePaths}
              movingFolderIds={movingFolderIds}
              setMovingFolderIds={setMovingFolderIds}
              onOptimisticRemoveFiles={handleOptimisticRemoveFiles}
              onOptimisticRemoveFolders={handleOptimisticRemoveFolders}
              removeFolderIds={removeFolderIds}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileManagement;
